import { ChevronRight, Dices, Search, SlidersHorizontal, Sparkles, Wand2 } from "lucide-react";
import { useMemo, useState } from "react";
import { contrastRatio, grade } from "@/color/contrast";
import { harmonizeAccents, keepLocked, randomPalette } from "@/color/generate";
import {
  ACCENT_KEYS,
  BRIGHT_KEYS,
  NEUTRAL_KEYS,
  PALETTE_INFO,
  type PaletteKey,
} from "@/model/palette";
import { type ColorId, describeExpr, effectiveExpr, hexOf } from "@/model/resolve";
import { GROUPS, type GroupId, TOKENS, type TokenDef } from "@/model/tokens";
import { useEditor } from "@/store/editor";
import { useUi } from "@/store/ui";
import { Button } from "@/ui/kit/button";
import { cn } from "@/ui/kit/cn";
import { Tip } from "@/ui/kit/tooltip";
import { GradeDot } from "../controls/badges";
import { Swatch } from "../controls/swatch";

function PaletteGroup({ title, keys }: { title: string; keys: readonly PaletteKey[] }) {
  const palette = useEditor((s) => s.draft.palette);
  const locks = useEditor((s) => s.draft.locks);
  const selection = useEditor((s) => s.selection);
  const select = useEditor((s) => s.select);
  return (
    <div>
      <div className="mb-1.5 text-[11px] text-faint">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {keys.map((k) => (
          <Tip key={k} label={`${PALETTE_INFO[k].label} · ${palette[k]}`} side="top">
            <Swatch
              hex={palette[k]}
              locked={locks.includes(k)}
              selected={selection?.kind === "palette" && selection.key === k}
              onClick={() => select({ kind: "palette", key: k })}
              aria-label={`${PALETTE_INFO[k].label} ${palette[k]}`}
              data-testid={`swatch-${k}`}
            />
          </Tip>
        ))}
      </div>
    </div>
  );
}

function PaletteSection() {
  const edit = useEditor((s) => s.edit);
  const openTool = useUi((s) => s.openTool);
  return (
    <section className="space-y-3 border-line border-b px-3 pt-3 pb-3.5">
      <div className="flex items-center gap-1">
        <h2 className="font-semibold text-[13px]">Palette</h2>
        <span className="flex-1" />
        <Tip label="Generate from a seed color">
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={() => openTool("generate")}
            aria-label="Generate"
          >
            <Sparkles />
          </Button>
        </Tip>
        <Tip label="Adjust hue, chroma, contrast, warmth">
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={() => openTool("adjust")}
            aria-label="Adjust"
          >
            <SlidersHorizontal />
          </Button>
        </Tip>
        <Tip label="Harmonize accents (equal lightness and chroma)">
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label="Harmonize accents"
            onClick={() => edit((t) => ({ ...t, palette: harmonizeAccents(t.palette, t.locks) }))}
          >
            <Wand2 />
          </Button>
        </Tip>
        <Tip label="Randomize unlocked swatches" keys="Space">
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label="Randomize"
            onClick={() =>
              edit((t) => ({
                ...t,
                palette: keepLocked(t.palette, randomPalette(t.variant), t.locks),
              }))
            }
          >
            <Dices />
          </Button>
        </Tip>
      </div>
      <PaletteGroup title="Neutrals" keys={NEUTRAL_KEYS} />
      <PaletteGroup title="Accents" keys={ACCENT_KEYS} />
      <PaletteGroup title="Bright ANSI" keys={BRIGHT_KEYS} />
    </section>
  );
}

function TokenRow({ def }: { def: TokenDef }) {
  const color = useEditor((s) => hexOf(s.resolved, def.id));
  const against = useEditor((s) =>
    def.contrast ? hexOf(s.resolved, def.contrast.against as ColorId) : undefined,
  );
  const thresholds = useEditor((s) => s.thresholds);
  const overridden = useEditor((s) => def.id in s.draft.overrides);
  const source = useEditor((s) => {
    const e = effectiveExpr(s.draft, def.id);
    return e ? describeExpr(e) : "";
  });
  const selected = useEditor((s) => s.selection?.kind === "token" && s.selection.id === def.id);
  const select = useEditor((s) => s.select);
  const g = against
    ? grade(contrastRatio(color, against), thresholds[def.contrast?.level ?? "text"])
    : undefined;
  return (
    <button
      type="button"
      onClick={() => select({ kind: "token", id: def.id })}
      className={cn(
        "group flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-xs",
        selected ? "bg-raised text-fg" : "text-muted hover:bg-raised/60 hover:text-fg",
      )}
      data-testid={`token-${def.id}`}
    >
      <span
        className="size-3.5 shrink-0 rounded-[4px] ring-1 ring-black/40"
        style={{ background: color }}
      />
      <span className="min-w-0 flex-1 truncate">{def.label}</span>
      {overridden && (
        <span className="size-1.5 shrink-0 rounded-full bg-accent" title="Overridden" />
      )}
      {g && g !== "pass" && <GradeDot grade={g} />}
      <span className="max-w-[45%] shrink-0 truncate font-mono text-[10px] text-faint">
        {source}
      </span>
    </button>
  );
}

function GroupSection({
  id,
  label,
  tokens,
  open,
  onToggle,
}: {
  id: GroupId;
  label: string;
  tokens: TokenDef[];
  open: boolean;
  onToggle: () => void;
}) {
  const sections = useMemo(() => {
    const out = new Map<string, TokenDef[]>();
    for (const t of tokens) out.set(t.section, [...(out.get(t.section) ?? []), t]);
    return [...out.entries()];
  }, [tokens]);
  const overrides = useEditor((s) => tokens.filter((t) => t.id in s.draft.overrides).length);
  return (
    <div className="border-line border-b">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-1.5 px-3 py-2 text-left hover:bg-raised/40"
        data-testid={`group-${id}`}
      >
        <ChevronRight
          className={cn("size-3.5 text-faint transition-transform", open && "rotate-90")}
        />
        <span className="font-medium text-[13px]">{label}</span>
        <span className="text-[11px] text-faint">{tokens.length}</span>
        <span className="flex-1" />
        {overrides > 0 && <span className="text-[10px] text-accent">{overrides} edited</span>}
      </button>
      {open && (
        <div className="space-y-2 px-1.5 pb-2.5">
          {sections.map(([section, defs]) => (
            <div key={section}>
              <div className="px-2 pb-0.5 text-[10px] text-faint uppercase tracking-wider">
                {section}
              </div>
              {defs.map((d) => (
                <TokenRow key={d.id} def={d} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar() {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<Record<GroupId, boolean>>({
    terminal: true,
    syntax: true,
    ui: false,
    herdr: false,
    claude: false,
    weechat: false,
  });
  const q = query.trim().toLowerCase();
  const filtered = (g: GroupId) =>
    TOKENS.filter(
      (t) =>
        t.group === g &&
        (!q ||
          t.label.toLowerCase().includes(q) ||
          t.id.toLowerCase().includes(q) ||
          t.section.toLowerCase().includes(q)),
    );
  return (
    <aside className="flex h-full min-h-0 w-[19rem] shrink-0 flex-col border-line border-r bg-panel">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <PaletteSection />
        <div className="sticky top-0 z-10 border-line border-b bg-panel px-3 py-2">
          <label className="flex h-8 items-center gap-2 rounded-md border border-line bg-bg px-2 text-muted focus-within:border-accent">
            <Search className="size-3.5" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter tokens…"
              className="w-full bg-transparent text-fg text-xs outline-none placeholder:text-faint"
            />
          </label>
        </div>
        {GROUPS.map((g) => {
          const tokens = filtered(g.id);
          if (q && tokens.length === 0) return null;
          return (
            <GroupSection
              key={g.id}
              id={g.id}
              label={g.label}
              tokens={tokens}
              open={q ? true : open[g.id]}
              onToggle={() => setOpen((o) => ({ ...o, [g.id]: !o[g.id] }))}
            />
          );
        })}
      </div>
    </aside>
  );
}
